import React, { Component } from "react";
import Paper from "@mui/material/Paper";
import { EditingState, ViewState } from "@devexpress/dx-react-scheduler";
import {
    Scheduler,
    DayView,
    WeekView,
    Appointments,
    DateNavigator,
    Toolbar,
    TodayButton,
    AppointmentTooltip,
    ConfirmationDialog,
    AppointmentForm,
    DragDropProvider,
    AllDayPanel,
    MonthView,
    ViewSwitcher,
    Resources,
    EditRecurrenceMenu,
} from "@devexpress/dx-react-scheduler-material-ui";
import { cmsAccount, jsonHeaders } from "../components/NavMenu";
import { colors } from "@mui/material";

const dragDisableIds = new Set([]);

const allowDrag = ({ id }) => !dragDisableIds.has(id);
const playlistColors = [colors.amber, colors.pink, colors.purple, colors.deepOrange, colors.teal, colors.green];
const groupColors = ["#7E57C2", "#FF7043", "#E91E63", "#E91E63", "#AB47BC", "#FFA726"];
const intervalOptions = [
    { id: 0, text: "weeks" },
    { id: 1, text: "days" },
    { id: 2, text: "hours" },
    { id: 3, text: "minutes" },
];

export class Schedule extends Component {
    constructor(props) {
        super(props);

        this.state = {
            data: [],
            currentDate: new Date(),
            groups: [],
            resources: [
                {
                    fieldName: "playlistUUID",
                    title: "Playlist",
                    instances: [],
                },
                {
                    fieldName: "groups",
                    title: "Groups",
                    instances: [],
                    allowMultiple: true,
                },
            ],
        };
        this.currentDateChange = (currentDate) => {
            this.setState({ currentDate });
        };
        this.commitChanges = this.commitChanges.bind(this);
    }

    componentDidMount() {
        const intervalId = setInterval(() => {
            if (cmsAccount) {
                this.fetchData();
                clearInterval(intervalId);
            }
        }, 100);
    }

    fetchSchedules() {
        var requestOptions = {
            method: "GET",
            headers: jsonHeaders,
            redirect: "follow",
        };

        fetch("/api/schedules", requestOptions)
            .then((res) => res.json())
            .then((result) => {
                console.log(result);
                result.forEach((schedule, index) => {
                    schedule.groups = JSON.parse(schedule.groups);
                });
                this.setState({ data: result });
            });
    }

    commitChanges({ added, changed, deleted }) {
        this.setState((state) => {
            let { data } = state;
            if (added) {
                const startingAddedId = data.length > 0 ? data[data.length - 1].id + 1 : 0;
                data = [...data, { id: startingAddedId, ...added }];
            }
            if (changed) {
                data = data.map((appointment) =>
                    changed[appointment.id] ? { ...appointment, ...changed[appointment.id] } : appointment
                );
            }
            if (deleted !== undefined) {
                data = data.filter((appointment) => appointment.id !== deleted);
            }
            this.updateSchedules(data);
            console.log(data);
            return { data };
        });
    }

    updateSchedules(data) {
        const jsonData = JSON.parse(JSON.stringify(data));
        jsonData.forEach((schedule) => {
            schedule.groups = JSON.stringify(schedule.groups);
        });
        var requestOptions = {
            method: "PUT",
            headers: jsonHeaders,
            body: JSON.stringify(jsonData),
            redirect: "follow",
        };
        fetch("/api/schedules", requestOptions)
            .then((res) => res.json())
            .then((result) => {
                console.log(result);
            });
    }

    DateEditorComponent({ excludeTime, ...restProps }) {
        const dateFormat = excludeTime ? "YYYY/MM/DD" : "YYYY/MM/DD HH:mm";
        return <AppointmentForm.DateEditor {...restProps} excludeTime={excludeTime} inputFormat={dateFormat} />;
    }

    BoolEditor(props) {
        if (props.label === "Repeat") {
            return <AppointmentForm.BooleanEditor {...props} readOnly />;
        } else {
            return <AppointmentForm.BooleanEditor {...props} />;
        }
    }

    LabelComponent(props) {
        if (props.text === "More Information") {
            return <AppointmentForm.Label {...props} text="Content Setting" className="p-0 mt-2" />;
        } else {
            return <AppointmentForm.Label {...props} />;
        }
    }

    InputComponent(props) {
        if (props.type === "multilineTextEditor") {
            return null;
        } else {
            return <AppointmentForm.TextEditor {...props} />;
        }
    }

    BasicLayout({ onFieldChange, appointmentData, ...restProps }) {
        const unit = intervalOptions.find((o) => o.text == appointmentData.intervalUnit)?.id;
        const calNewInterval = (option, value) => {
            switch (option) {
                case "weeks":
                    return value * 7 * 24 * 60;
                case "days":
                    return value * 24 * 60;
                case "hours":
                    return value * 60;
                case "minutes":
                    return value;
                default:
                    return value * 60;
            }
        };
        const onInputChange = (value) => {
            const newInterval = calNewInterval(appointmentData.intervalUnit, value);
            if (newInterval >= 5) onFieldChange({ intervalValue: value, interval: newInterval });
        };
        const onSelectChange = (value) => {
            const newInterval = calNewInterval(intervalOptions[value].text, appointmentData.intervalValue);
            if (newInterval >= 5) onFieldChange({ intervalUnit: intervalOptions[value].text, interval: newInterval });
        };

        return (
            <AppointmentForm.BasicLayout appointmentData={appointmentData} onFieldChange={onFieldChange} {...restProps}>
                <AppointmentForm.Label text="Image-change Interval" type="title" className="mt-3" />
                <AppointmentForm.TextEditor
                    className="w-25"
                    type="numberEditor"
                    value={appointmentData.intervalValue}
                    onValueChange={onInputChange}
                    placeholder="Interval"
                />
                <AppointmentForm.Select
                    className="ms-3"
                    availableOptions={intervalOptions}
                    onValueChange={onSelectChange}
                    value={unit ?? 1}
                />
                <AppointmentForm.Label
                    text="* Must be at least 5 minutes."
                    type="ordinary"
                    style={{ color: "#42a5f5", fontSize: "12px" }}
                />
            </AppointmentForm.BasicLayout>
        );
    }

    async fetchData() {
        var requestOptions = {
            method: "GET",
            headers: jsonHeaders,
            redirect: "follow",
        };

        const response = await fetch("/api/groups", requestOptions);
        const result = await response.json();
        const groups = [];
        result.forEach((group, index) => {
            groups.push({
                text: group.name,
                id: group.uuid,
                color: groupColors[index % groupColors.length],
            });
        });

        const response2 = await fetch("/api/playlists", requestOptions);
        const result2 = await response2.json();
        const playlists = [];
        result2.forEach((playlist, index) => {
            playlists.push({
                text: playlist.name,
                id: playlist.uuid,
                color: playlistColors[index % playlistColors.length],
            });
        });

        this.setState({
            resources: [
                {
                    fieldName: "playlistUUID",
                    title: "Playlist",
                    instances: playlists,
                },
                {
                    fieldName: "groups",
                    title: "Groups",
                    instances: groups,
                    allowMultiple: true,
                },
            ],
        });
        this.fetchSchedules();
    }

    render() {
        const { data, currentDate, resources } = this.state;
        return (
            <>
                <Paper style={{ marginLeft: "100px", zIndex: 0 }}>
                    <Scheduler data={data} height={674}>
                        <ViewState currentDate={currentDate} onCurrentDateChange={this.currentDateChange} />
                        <EditingState onCommitChanges={this.commitChanges} />
                        <EditRecurrenceMenu />
                        <MonthView />
                        <WeekView startDayHour={0} endDayHour={24} cellDuration={60} />
                        <AllDayPanel />
                        <Toolbar />
                        <ViewSwitcher />
                        <DateNavigator />
                        <TodayButton />
                        {/* <ConfirmationDialog /> */}
                        <Appointments />
                        <AppointmentTooltip showOpenButton showDeleteButton />
                        <AppointmentForm
                            basicLayoutComponent={this.BasicLayout}
                            // booleanEditorComponent={this.BoolEditor}
                            labelComponent={this.LabelComponent}
                            textEditorComponent={this.InputComponent}
                            dateEditorComponent={this.DateEditorComponent}
                        />
                        <Resources data={resources} mainResourceName="playlistUUID" />
                        <DragDropProvider allowDrag={allowDrag} />
                    </Scheduler>
                </Paper>
            </>
        );
    }
}
